/** Purchase order list → Create Shipment eligibility and copy. */

export const CREATE_SHIPMENT_PO_HEADER_STATUSES = ["Fully Allocated", "Cargo Ready"];

/**
 * Whether a purchase order is the dropship type.
 *
 * @param {object|undefined} order
 * @return {boolean}
 */
const isDropshipPurchaseOrder = order => {
  const type = order?.type;
  if (!type) {
    return false;
  }
  if (typeof type === "object") {
    return type.id === "dropship";
  }
  return type === "dropship";
};

/**
 * @param {Array<{ id: number, status: string, name?: string, user_reference?: string }>} orders
 * @returns {{ ok: boolean, reason: string }}
 */
export const createShipmentSelectionState = orders => {
  if (!orders?.length) {
    return { ok: false, reason: "Select at least one purchase order." };
  }

  const dropship = orders.filter(order => isDropshipPurchaseOrder(order));
  if (dropship.length) {
    return {
      ok: false,
      reason: "Cannot create a shipment for a dropship purchase order.",
    };
  }

  const ineligible = orders.filter(
    order => !CREATE_SHIPMENT_PO_HEADER_STATUSES.includes(order.status),
  );
  if (ineligible.length) {
    const label = ineligible[0].user_reference || ineligible[0].name || `PO ${ineligible[0].id}`;
    return {
      ok: false,
      reason: `Only Fully Allocated or Cargo Ready orders can create shipments (${label} is ${ineligible[0].status}).`,
    };
  }

  return { ok: true, reason: "" };
};

/**
 * @param {number|string} qty
 * @returns {number}
 */
export const toQtyNumber = qty => {
  const value = Number(qty);
  return Number.isFinite(value) ? value : 0;
};

/** @const {number} Max length for consignment reference (shipment name). */
export const SHIPMENT_REFERENCE_MAX_LENGTH = 100;

/**
 * Remaining quantity available to ship on a preview line.
 *
 * @param {{ ship_qty?: number|string, allocated_qty?: number|string }} line
 * @returns {number}
 */
export const lineMaxShipQty = line => toQtyNumber(line.ship_qty ?? line.allocated_qty);

/**
 * @param {Record<number, string>|undefined} lineQuantities
 * @param {number} lineId
 * @param {{ ship_qty?: number|string, allocated_qty?: number|string }} line
 * @returns {string}
 */
export const getEditedLineQtyText = (lineQuantities, lineId, line) => {
  if (lineQuantities && lineQuantities[lineId] !== undefined) {
    return String(lineQuantities[lineId]);
  }
  return String(lineMaxShipQty(line));
};

/**
 * @param {string|number|undefined} value
 * @returns {number|null}
 */
export const parseLineQtyInput = value => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return null;
  }
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
};

/**
 * @param {string|number|undefined} qtyInput
 * @param {number} maxQty
 * @returns {{ valid: boolean, qty?: number }}
 */
export const validateCheckedLineQty = (qtyInput, maxQty) => {
  const parsed = parseLineQtyInput(qtyInput);
  if (parsed === null || parsed <= 0 || parsed > maxQty) {
    return { valid: false };
  }
  return { valid: true, qty: parsed };
};

/**
 * Effective ship quantity for a checked line (for summary and API).
 *
 * @param {Record<number, string>|undefined} lineQuantities
 * @param {number} lineId
 * @param {{ ship_qty?: number|string, allocated_qty?: number|string }} line
 * @returns {number}
 */
export const effectiveLineShipQty = (lineQuantities, lineId, line) => {
  const check = validateCheckedLineQty(getEditedLineQtyText(lineQuantities, lineId, line), lineMaxShipQty(line));
  if (check.valid) {
    return check.qty;
  }
  return 0;
};

/**
 * @param {{
 *   ids: number[],
 *   lineQuantities?: Record<number, string>,
 *   linesById: Record<number, object>,
 *   reference?: string,
 * }} params
 * @returns {{ ids: number[], quantities?: Record<string, string>, reference?: string }}
 */
export const buildCreatePurchaseShipmentPayload = ({ ids, lineQuantities, linesById, reference }) => {
  const payload = { ids: [...ids] };
  const quantities = {};

  ids.forEach(id => {
    const line = linesById[id];
    if (!line) {
      return;
    }
    const text = getEditedLineQtyText(lineQuantities, id, line).trim();
    quantities[String(id)] = text;
  });

  if (Object.keys(quantities).length) {
    payload.quantities = quantities;
  }

  const ref = reference?.trim();
  if (ref) {
    payload.reference = ref.slice(0, SHIPMENT_REFERENCE_MAX_LENGTH);
  }

  return payload;
};

/**
 * Destination warehouse ids present on a shipment preview.
 *
 * @param {Array<{ warehouse_id?: number }>|undefined} groups
 * @returns {number[]}
 */
export const previewDestinationWarehouseIds = groups => {
  const ids = [];
  const seen = new Set();
  (groups || []).forEach(group => {
    const id = group?.warehouse_id;
    if (id == null || seen.has(id)) {
      return;
    }
    seen.add(id);
    ids.push(id);
  });
  return ids;
};

/**
 * Keep shipments whose destination is one of the preview warehouses.
 *
 * @param {Array<{ to_warehouse_id?: number }>|undefined} shipments
 * @param {number[]} warehouseIds
 * @returns {Array<{ to_warehouse_id?: number }>}
 */
export const filterShipmentsByDestinations = (shipments, warehouseIds) => {
  const allowed = new Set(warehouseIds || []);
  return (shipments || []).filter(shipment => allowed.has(shipment.to_warehouse_id));
};

/**
 * Label for a shipment option: reference, number, and destination.
 *
 * @param {{ id?: number, name?: string, reference?: string, to_warehouse_name?: string }} shipment
 * @returns {string}
 */
export const shipmentOptionLabel = shipment => {
  const name = String(shipment?.name || "").trim();
  const reference = String(shipment?.reference || "").trim();
  const title =
    reference && name && reference.toLowerCase() !== name.toLowerCase()
      ? `${reference} (${name})`
      : reference || name || `Shipment ${shipment?.id ?? ""}`;
  const warehouse = String(shipment?.to_warehouse_name || "").trim();
  return warehouse ? `${title} · ${warehouse}` : title;
};

/**
 * Whether a typed query matches a shipment name, number, or destination.
 *
 * @param {{ id?: number, name?: string, reference?: string, to_warehouse_name?: string }|undefined} shipment
 * @param {string} query
 * @returns {boolean}
 */
export const shipmentMatchesSearch = (shipment, query) => {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) {
    return true;
  }
  if (!shipment) {
    return false;
  }
  return [shipment.name, shipment.reference, shipment.to_warehouse_name, shipment.id]
    .filter(value => value != null && String(value).trim() !== "")
    .some(value => String(value).toLowerCase().includes(needle));
};

/**
 * Line ids that belong to one destination warehouse.
 *
 * @param {Array<{ warehouse_id?: number, lines?: Array<{ line_id: number }> }>|undefined} groups
 * @param {number|null|undefined} warehouseId
 * @returns {number[]}
 */
export const lineIdsForShipmentDestination = (groups, warehouseId) => {
  if (warehouseId == null) {
    return [];
  }
  const ids = [];
  (groups || []).forEach(group => {
    if (group.warehouse_id !== warehouseId) {
      return;
    }
    (group.lines || []).forEach(line => {
      ids.push(line.line_id);
    });
  });
  return ids;
};
